import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Pencil, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/PageHeader";
import { api, errMsg, money } from "@/lib/api";

const BLANK = { name: "", email: "", position: "", phone: "", hourly_rate: 0, tenant_role: "staff", active: true };

const StaffForm = ({ member, onSaved }) => {
  const [f, setF] = useState(member);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    try {
      if (member.id) {
        const { name, position, phone, tenant_role, active } = f;
        await api.put(`/biz/staff/${member.id}`, { name, position, phone, tenant_role, active, hourly_rate: Number(f.hourly_rate) });
        onSaved(null);
      } else {
        const { data } = await api.post("/biz/staff", { ...f, hourly_rate: Number(f.hourly_rate) });
        onSaved(data);
      }
      toast.success("Saved");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <form onSubmit={save} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2 space-y-1.5"><Label>Full name</Label><Input required value={f.name} onChange={set("name")} data-testid="staff-name-input" /></div>
        <div className="col-span-2 space-y-1.5"><Label>Email (login)</Label><Input required type="email" disabled={!!member.id} value={f.email} onChange={set("email")} data-testid="staff-email-input" /></div>
        <div className="space-y-1.5"><Label>Position</Label><Input value={f.position} onChange={set("position")} data-testid="staff-position-input" /></div>
        <div className="space-y-1.5"><Label>Hourly rate £</Label><Input type="number" step="0.01" min="0" value={f.hourly_rate} onChange={set("hourly_rate")} data-testid="staff-rate-input" /></div>
        <div className="space-y-1.5"><Label>Phone</Label><Input value={f.phone || ""} onChange={set("phone")} data-testid="staff-phone-input" /></div>
        <div className="space-y-1.5">
          <Label>Access</Label>
          <Select value={f.tenant_role} onValueChange={(v) => setF({ ...f, tenant_role: v })}>
            <SelectTrigger data-testid="staff-role-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="staff">Staff</SelectItem><SelectItem value="admin">Manager (admin)</SelectItem></SelectContent>
          </Select>
        </div>
      </div>
      {member.id && <label className="flex items-center gap-2 text-sm"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="staff-active-switch" />Active</label>}
      <Button type="submit" className="w-full rounded-full" data-testid="staff-save-btn">Save</Button>
    </form>
  );
};

export default function Staff() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creds, setCreds] = useState(null);
  const load = useCallback(() => {
    api.get("/biz/staff").then((r) => setRows(r.data));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const resetPw = async (m) => {
    try {
      const { data } = await api.post(`/biz/staff/${m.id}/reset-password`);
      setCreds({ email: m.email, password: data.temp_password });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <PageHeader title="Staff" subtitle={`${rows.filter((r) => r.active).length} active team members`}>
        <Button className="rounded-full" onClick={() => setEditing(BLANK)} data-testid="add-staff-btn"><Plus className="mr-1 h-4 w-4" />Add staff</Button>
      </PageHeader>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Position</TableHead><TableHead>Rate</TableHead><TableHead>Access</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {rows.map((m) => (
              <TableRow key={m.id} className={m.active ? "" : "opacity-50"} data-testid={`staff-row-${m.id}`}>
                <TableCell><p className="font-semibold">{m.name}</p><p className="text-xs text-slate-500">{m.email}</p></TableCell>
                <TableCell>{m.position}</TableCell>
                <TableCell className="font-mono">{money(m.hourly_rate)}/h</TableCell>
                <TableCell><Badge variant={m.tenant_role === "admin" ? "default" : "secondary"}>{m.tenant_role === "admin" ? "Manager" : "Staff"}</Badge>{!m.active && <Badge variant="outline" className="ml-1">Inactive</Badge>}</TableCell>
                <TableCell className="space-x-1 text-right">
                  <Button size="icon" variant="ghost" onClick={() => resetPw(m)} title="Reset password" data-testid={`staff-reset-pw-btn-${m.id}`}><KeyRound className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => setEditing(m)} data-testid={`staff-edit-btn-${m.id}`}><Pencil className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="staff-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit team member" : "Add team member"}</DialogTitle></DialogHeader>
          {editing && <StaffForm member={editing} onSaved={(created) => { setEditing(null); load(); if (created) setCreds({ email: created.email, password: created.temp_password }); }} />}
        </DialogContent>
      </Dialog>
      <Dialog open={!!creds} onOpenChange={(o) => !o && setCreds(null)}>
        <DialogContent data-testid="staff-credentials-dialog">
          <DialogHeader><DialogTitle>Login details</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Share these with your team member. They're shown once.</p>
          <div className="rounded-lg bg-slate-950 p-4 font-mono text-sm text-white">
            <p data-testid="staff-cred-email">{creds?.email}</p>
            <p data-testid="staff-cred-password">{creds?.password}</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
